<template>
  <div class="w-full pt-2 flex flex-col flex-auto justify-between">
    <WidgetMerchantLogo />
    <div class="mb-3">
      <h1 class="h1 font-semibold text-xl mb-2">Make Payment</h1>
      <p class="text-sm font-normal mb-2">How much do you want to contribute?</p>
    </div>
    <div class="py-2">
      <hr class="h-px m-0 bg-gray-200 border-0" />
      <CurrencyInputBig v-model="amount" :min="1" :max="maxAmount" autofocus />
      <hr class="h-px m-0 bg-gray-200 border-0" />
    </div>
    <div
      v-if="widgetStore.order.state === 'PARTIAL' && !isPayingFullAmount"
      class="flex justify-center items-center my-4"
    >
      <Button variant="outline" @click="onBreakdown()" data-testid="paymentPlanBreakdown">
        Payment breakdown <ChevronRight />
      </Button>
    </div>
    <div>
      <div class="flex justify-between items-center py-2">
        <p class="text-sm font-bold">{{ label }}</p>
        <p class="text-sm font-normal">{{ displayCurrency(widgetStore.order.amountDue) }}</p>
      </div>
      <div class="flex justify-between items-center py-2">
        <p class="text-sm font-bold">Outstanding</p>
        <p class="text-sm font-normal">{{ displayCurrency(outstandingAmount) }}</p>
      </div>
      <div class="flex gap-2 pt-2">
        <button
          @click="back"
          variant="outline-secondary"
          class="mt-6 w-full bg-white font-bold py-2 rounded-lg border-slate-200 border-2 hover:bg-gray-100"
        >
          Back
        </button>
        <button
          @click="next"
          variant="outline-secondary"
          class="mt-6 w-full bg-brand-500 text-white font-bold py-2 rounded-lg hover:bg-brand-700 disabled:opacity-60 disabled:bg-brand-600"
          :disabled="isNextDisabled"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CurrencyInputBig from '@/components/common/CurrencyInputBig.vue';
import WidgetMerchantLogo from '@/components/common/WidgetMerchantLogo.vue';
import { Button } from '@/components/ui/button';
import { useGeneralStore } from '@/stores/general';
import { useWidgetStore } from '@/stores/widget';
import { displayCurrency, toCents, toRand } from '@/utils/format.utils';
import { ChevronRight } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { round } from '../../../utils/lowDash';
import { upcomingPayments } from '../../../utils/paymentPlan.utils';

const router = useRouter();
const widgetStore = useWidgetStore();
const label = ref(widgetStore.order.type === 'REWARDS' ? 'Savings Goal' : 'Order Total');
const generalStore = useGeneralStore();
const upcoming = upcomingPayments(widgetStore.plan);
const recommendedAmount =
  upcoming.length > 0 && upcoming[0]?.amount > 100 ? upcoming[0].amount : (upcoming[1]?.amount ?? 0);
const dueAmount = upcoming.length > 0 ? recommendedAmount : 0;
const amount = ref(
  widgetStore.boost.newBoost.deposit > 0 ? toRand(widgetStore.boost.newBoost.deposit) : round(dueAmount / 100, 2),
);
const isNextDisabled = ref(false);

const outstandingAmount = upcoming
  ? upcoming.reduce((acc, payment) => {
      return acc + payment.amount;
    }, 0)
  : 0;
const maxAmount = round(outstandingAmount / 100, 2);

watch(amount, (value) => {
  if (value < 1) {
    isNextDisabled.value = true;
  } else if (value > maxAmount) {
    isNextDisabled.value = true;
  } else {
    isNextDisabled.value = false;
  }
});

const isPayingFullAmount = computed(() => {
  return toCents(amount.value) === outstandingAmount;
});

const back = () => {
  if (generalStore.iFrame) {
    window.parent.postMessage({ type: 'layup-close' }, '*');
  } else {
    window.history.back();
  }
};

const next = () => {
  if (widgetStore && widgetStore.plan) {
    widgetStore.amountDue = toCents(amount.value);
    widgetStore.id = widgetStore.plan._id;
    widgetStore.payAtReference = widgetStore.plan.payAtReferenceNumber || '';
    widgetStore.boost.newBoost.deposit = 0;
    router.push({
      name: 'SelectPaymentMethod',
    });
  }
};

const onBreakdown = () => {
  widgetStore.boost.newBoost.deposit = toCents(amount.value);
  router.push({
    name: 'BoostPaymentBreakdown',
  });
};
</script>
